import React, { useState } from 'react';

export const FAQ = ({
  sectionTitle = "Frequently Asked Questions",
  backgroundColor = "#faf9f9",
  onReadStoryClick,
  faq1Question = "What is Real Patients™?",
  faq1Answer = "Real Patients™ is a place where people living (or caring for someone) with a health condition share their stories in order to support the community and improve research, treatments, and care.",
  faq2Question = "How is Real Patients™ different?",
  faq2Answer = "This isn't another forum or social media group—and it's not the same old surveys you're handed at a doctor's office. Real Patients™ is designed to listen to patient experiences and bring them into healthcare decisions in a structured, respectful way, and reward patients for doing so. You don't have to advocate, post publicly, or engage constantly. You just share when it makes sense for you.",
  faq3Question = "How do I get rewarded?",
  faq3Answer = "Compensation varies by opportunity and reflects the time and type of participation involved. You'll always know what to expect before you participate.",
  faq4Question = "How quickly will I receive my rewards?",
  faq4Answer = "Most rewards are processed within 5 business days of completing an activity. We offer multiple options including direct deposit, PayPal, and gift cards. No waiting weeks or chasing down what you've earned—we respect your time.",
  faq5Question = "Will my story be public or private?",
  faq5Answer = "Your experience is private by default. Most sharing happens confidentially—your responses are used to help improve understanding, without your name attached. Occasionally organizations are looking for speakers and testimonials, but nothing happens without your consent.",
  faq6Question = "Who sees my data?",
  faq6Answer = "Only approved research partners see your de-identified, anonymous responses. Your personal contact information stays with us and is protected by bank-level encryption.",
  faq7Question = "What if my condition is rare, are there opportunities for me?",
  faq7Answer = "We work with patients across 2,500+ health conditions—including rare diseases. In fact, rare disease patients are often in high demand for research because the condition is not well understood. If your condition isn't listed, sign up anyway—new opportunities are added weekly.",
  faq8Question = "Can I stop participating at any time?",
  faq8Answer = "Absolutely. There are no commitments, contracts, or penalties for leaving. You can unsubscribe from emails in one click, decline any opportunity, or delete your account entirely at any time. You're always in control of your participation.",
  faq9Question = "How much time does it take?",
  faq9Answer = "Opportunities vary and participation is flexible to fit your schedule. Activities range from quick 5-minute surveys to longer interviews to ongoing community engagement. You choose which opportunities to participate in based on your availability and interests.",
  faq10Question = "Is this legitimate?",
  faq10Answer = "Yes. Real Patients™ was founded by patients with deep experience in healthcare research and innovation. Our team is PhD-trained and community builders, and we've worked with pharmaceutical companies, academic institutions, and advisory groups shaping healthcare research and policy. Our work has been featured by STAT News, FierceBiotech, CNBC, and Entrepreneur Magazine, which recognized our founders for pioneering an innovative way to work with patients. Most importantly, we're built on trust—transparent, consent-based, and centered on patients.",
  faq11Question = "How do you verify patients?",
  faq11Answer = "We verify patients to protect the community and make sure experiences are authentic. Verification is designed to be simple and secure—and so you don't have to re-verify your information again and again with different organizations. Everything you share is protected, handled securely, and only shared with your consent. You'll only be asked to confirm what's necessary.",
  faq12Question = "Is Real Patients™ the same as Savvy Cooperative?",
  faq12Answer = "Real Patients™ is the evolution of Savvy Cooperative. We changed our name to better reflect what we've always been built around: real patient experiences, shared directly by the people living them. The same patient-led team and values are behind Real Patients™—just with a clearer focus on listening to patients and bringing their experiences into healthcare decisions in meaningful ways."
}) => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { question: faq1Question, answer: faq1Answer },
    { question: faq2Question, answer: faq2Answer },
    { question: faq3Question, answer: faq3Answer },
    { question: faq4Question, answer: faq4Answer },
    { question: faq5Question, answer: faq5Answer },
    { question: faq6Question, answer: faq6Answer },
    { question: faq7Question, answer: faq7Answer },
    { question: faq8Question, answer: faq8Answer },
    { question: faq9Question, answer: faq9Answer },
    { question: faq10Question, answer: faq10Answer },
    { question: faq11Question, answer: faq11Answer },
    { question: faq12Question, answer: faq12Answer }
  ];

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      style={{
        backgroundColor: backgroundColor,
        padding: '100px 24px 120px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Wavy top */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '70px',
          pointerEvents: 'none'
        }}
        viewBox="0 0 1440 70"
        preserveAspectRatio="none"
      >
        <path
          d="M0,70 C180,30 360,60 540,40 C720,20 900,50 1080,30 C1260,10 1350,40 1440,25 L1440,0 L0,0 Z"
          fill="#f1eeee"
        />
      </svg>

      {/* Organic background shapes */}
      <div
        style={{
          position: 'absolute',
          top: '5%',
          right: '-10%',
          width: '400px',
          height: '400px',
          borderRadius: '55% 45% 60% 40% / 45% 55% 40% 60%',
          background: 'linear-gradient(180deg, rgba(129, 156, 209, 0.08) 0%, rgba(56, 92, 188, 0.04) 100%)',
          filter: 'blur(50px)',
          pointerEvents: 'none',
          transform: 'rotate(30deg)'
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '-5%',
          width: '350px',
          height: '350px',
          borderRadius: '45% 55% 40% 60% / 55% 45% 60% 40%',
          background: 'linear-gradient(135deg, rgba(250, 177, 132, 0.1) 0%, rgba(241, 91, 65, 0.05) 100%)',
          filter: 'blur(40px)',
          pointerEvents: 'none'
        }}
      />

      {/* Small decorative elements */}
      <div
        style={{
          position: 'absolute',
          top: '30%',
          left: '8%',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: 'rgba(56, 92, 188, 0.3)',
          pointerEvents: 'none'
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          right: '12%',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: 'rgba(241, 91, 65, 0.4)',
          pointerEvents: 'none'
        }}
      />

      <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <h2
          style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: 'clamp(28px, 5vw, 42px)',
            fontWeight: '600',
            color: '#272726',
            textAlign: 'center',
            marginTop: '0',
            marginBottom: '48px'
          }}
        >
          {sectionTitle}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {faqs.map((faq, index) => (
            <div
              key={index}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              <button
                onClick={() => toggleFaq(index)}
                style={{
                  width: '100%',
                  padding: '20px 24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span
                  style={{
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#272726'
                  }}
                >
                  {faq.question}
                </span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#385cbc"
                  strokeWidth="2"
                  style={{
                    transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                    flexShrink: 0,
                    marginLeft: '16px'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div
                style={{
                  maxHeight: openIndex === index ? '500px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease'
                }}
              >
                <div
                  style={{
                    padding: '0 24px 20px 24px',
                    margin: '0',
                    fontSize: '16px',
                    lineHeight: '1.7',
                    color: '#272726',
                    textAlign: 'left'
                  }}
                >
                  <p style={{ margin: 0 }}>{faq.answer}</p>
                  {faq.question === "Is this legitimate?" && onReadStoryClick && (
                    <button
                      onClick={onReadStoryClick}
                      style={{
                        display: 'inline-block',
                        marginTop: '16px',
                        padding: '10px 24px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#385cbc',
                        backgroundColor: 'transparent',
                        border: '2px solid #385cbc',
                        borderRadius: '24px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#385cbc';
                        e.currentTarget.style.color = '#ffffff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#385cbc';
                      }}
                    >
                      Read Our Story
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
